import { Form, InputField, Inspector, List, Tabs } from '@bynder/design-system';
import { useActiveWidget } from '../../../../queries/guides';
import { FigmaWidget, WIDGET_TYPE } from '../../../../types';
import { WidgetSettingsHeader } from '../../Styles';
import { useEffect, useState } from 'react';
import { Translate, useTranslate } from '@bynder/localization';
import { useFigmaWidgetMutations } from '../../../../mutations/widgets/figma';
import MediaBorderRadius from '../MediaBorderRadius';
import { emptyCSSObject } from '../../../../constants/widgets';
import { isDevelopment } from '../../../../constants/app';
import CustomCssStyleEditor from '../CustomCSSEditor';

export const FigmaWidgetSettings = () => {
  const { translate } = useTranslate();
  const figmaWidget = useActiveWidget() as FigmaWidget;
  const { editSource, editProperties } = useFigmaWidgetMutations();
  const { editStyle } = useFigmaWidgetMutations();
  const [fileUrl, setFileUrl] = useState<string>(
    figmaWidget?.sourceId
      ? `https://www.figma.com/${figmaWidget?.sourceId}`
      : '',
  );
  const isSourceEmpty = figmaWidget.sourceId === '';

  const handleEditStyle = (style: string) => {
    editStyle.mutate({ style });
  };

  const onAddUrl = (url: string) => {
    setFileUrl(url);
    const videoId = extractFigmaDetails(url);
    if (videoId) {
      editSource.mutate({ source: extractFigmaDetails(url) });
    }
  };

  useEffect(() => {
    setFileUrl(
      figmaWidget?.sourceId
        ? `https://www.figma.com/${figmaWidget?.sourceId}`
        : '',
    );
  }, [figmaWidget.sourceId]);

  return (
    <>
      <WidgetSettingsHeader widgetType={WIDGET_TYPE.FIGMA} />
      <List>
        <InputField
          isInvalid={extractFigmaDetails(fileUrl) === ''}
          helperText={extractFigmaDetails(fileUrl) === '' && 'Invalid URL'}
          onChange={onAddUrl}
          value={fileUrl}
          label="Figma URL"
          placeholder="https://www.figma.com/file/1VSVfKi6RQkgggdl75ZcB0"
        />
        <List.Item
          isDisabled={isSourceEmpty}
          checkedVariant="switch"
          isChecked={figmaWidget.settings.controls}
          onClick={() =>
            editProperties.mutate({
              property: { controls: !figmaWidget.settings.controls },
            })
          }
        >
          <Translate id="WIDGET_INSPECTOR.FIGMA_CONTROLS_TOGGLE" />
        </List.Item>
        <List.Item
          isDisabled={isSourceEmpty}
          isChecked={figmaWidget.settings.footer}
          checkedVariant="switch"
          onClick={() =>
            editProperties.mutate({
              property: { footer: !figmaWidget.settings.footer },
            })
          }
        >
          <Translate id="WIDGET_INSPECTOR.FIGMA_FOOTER_TOGGLE" />
        </List.Item>
      </List>
      <List.Divider />
      <Form.Row>
        <Form.Group>
          <List.Item>
            <Translate id="WIDGET_INSPECTOR.FIGMA_THEME_TITLE" />
          </List.Item>
          <Tabs variant="clean">
            <Tabs.Tab
              label={translate('WIDGET_INSPECTOR.FIGMA_SYSTEM_THEME')}
              isActive={figmaWidget.settings.theme === 'system'}
              onClick={() =>
                editProperties.mutate({
                  property: { theme: 'system' },
                })
              }
              isDisabled={isSourceEmpty}
            />
            <Tabs.Tab
              label={translate('WIDGET_INSPECTOR.FIGMA_LIGHT_THEME')}
              isActive={figmaWidget.settings.theme === 'light'}
              onClick={() =>
                editProperties.mutate({
                  property: { theme: 'light' },
                })
              }
              isDisabled={isSourceEmpty}
            />
            <Tabs.Tab
              label={translate('WIDGET_INSPECTOR.FIGMA_DARK_THEME')}
              isActive={figmaWidget.settings.theme === 'dark'}
              onClick={() =>
                editProperties.mutate({
                  property: { theme: 'dark' },
                })
              }
              isDisabled={isSourceEmpty}
            />
          </Tabs>
        </Form.Group>
      </Form.Row>
      <Inspector.Divider />
      <MediaBorderRadius
        style={figmaWidget?.style?.stylesheet ?? emptyCSSObject.stylesheet}
        onEditStyle={handleEditStyle}
      />
      {isDevelopment && (
        <>
          <Inspector.Divider />
          <CustomCssStyleEditor
            handleChange={() => editStyle.mutate({ style: '' })}
            label="Section CSS"
            placeholder="Enter your custom CSS here"
            style={figmaWidget?.style?.stylesheet}
          />
        </>
      )}
    </>
  );
};

function extractFigmaDetails(input: string) {
  const regex =
    /https:\/\/(?:www\.)?figma\.com\/(design|board|proto|file)\/([a-zA-Z0-9]+)(?:\/.*)?/;
  const match = input.match(regex);

  if (match && match[1] && match[2]) {
    return `${match[1]}/${match[2]}`;
  }

  return '';
}
